import { useEffect, useState } from 'react';
import clsx from 'clsx';

import { CollapserIcon } from './CollapserIcon';
import { LockIcon } from '../../assets/icons';

type CollapserProps = {
  long?: boolean;
  enable?: boolean;
  className?: string;
  withScroll?: boolean;
  header: React.ReactNode;
  forceOpen?: boolean;
  defaultExpanded?: boolean;
  contentClassName?: string;
  content?: React.ReactNode;
  children?: React.ReactNode;
  renderIcon?: ({ show }: { show: boolean }) => React.ReactNode;
  iconPosition?: 'left' | 'right';
  suffix?: boolean;
};

const defaultRenderIcon: CollapserProps['renderIcon'] = ({ show }) => (
  <CollapserIcon show={show} />
);

export const Collapser: React.FC<CollapserProps> = ({
  header,
  content,
  children,
  className,
  long = false,
  enable = true,
  contentClassName,
  withScroll = true,
  defaultExpanded = false,
  renderIcon = defaultRenderIcon,
  iconPosition = 'right',
  suffix = false,
  forceOpen,
}) => {
  const [show, setShow] = useState<boolean>(defaultExpanded ?? false);

  const handleToggle = () => setShow(s => !s);

  const open = !!show && !!enable;
  useEffect(() => {
    if (!forceOpen) return;

    setShow(true);
  }, [forceOpen]);

  return (
    <div className={clsx('', className)}>
      {!!header && (
        <div
          onClick={handleToggle}
          className={clsx(
            'p-md pl-sm',
            'cursor-pointer flex items-center justify-between font-bold text-medium',
            open ? 'bg-backgroundLight' : 'bg-backgroundLight',
            open ? 'border-b border-transparent' : 'border-b border-border',
            iconPosition === 'left' ? 'flex-row-reverse' : ''
          )}
        >
          <div className="flex items-center">
            {!!enable && (
              <div className="ml-xs mr-xs">{renderIcon({ show })}</div>
            )}
            <div className="text-left text-truncate">{header}</div>
          </div>
          {suffix && <LockIcon />}
        </div>
      )}
      <div
        className={clsx(
          open ? 'block' : 'hidden',
          contentClassName,
          withScroll ? 'overflow-y-auto' : '',
          long ? 'max-h-[175rem]' : 'max-h-[100rem]',
          'text-mainText text-small font-light text-left animate-expand transition-all'
        )}
      >
        {children ?? content}
      </div>
    </div>
  );
};
